import { uiMenuApi } from "@/services/backendApi";
import { isMenuAccessStale, touchMenuAccessLoadedAt } from "@/config/session";

/** Top-level app routes that require an explicit menu permission. */
export const MENU_GATED_ROUTE_NAMES = new Set([
  "Dashboard",
  "Tables",
  "Project Overview",
  "Project Overview All",
  "Project Overview My",
  "Test Management",
  "Test Plan",
  "Plan Management Team",
  "Plan Management",
  "Plan Management All",
  "Plan Management Team A",
  "Plan Management Team B",
  "Plan Management Team C",
  "QA Management",
  "QA Test Case",
  "QA Test Run",
  "QA Test Report",
  "My Tasks",
  "Profile",
  "User Setting",
  "Role Permission",
  "Management",
  "Automation Tracking",
  "System Setting",
  "Sprint Setting",
  "Menu Setting",
  "AI Prompt Setting",
]);

export function resolveMenuRouteTarget(menuRow, router) {
  const routeName = String(menuRow?.routeName || "").trim();
  const routePath = String(menuRow?.routePath || "").trim();
  if (routeName && router.hasRoute(routeName)) {
    return { name: routeName };
  }
  if (routePath) {
    const matched = router.resolve({ path: routePath });
    if (matched?.matched?.length && matched.name) {
      return { name: matched.name };
    }
  }
  return null;
}

function sortMenuRows(rows) {
  return [...(rows || [])].sort(
    (a, b) =>
      (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0) ||
      Number(a.menuId || 0) - Number(b.menuId || 0)
  );
}

export function getAllowedRouteNames(menuRows, router) {
  const names = new Set();
  for (const row of menuRows || []) {
    const target = resolveMenuRouteTarget(row, router);
    if (!target) continue;
    const resolved = router.resolve(target);
    if (resolved?.name) names.add(resolved.name);
  }
  return names;
}

/** First menu the user may open (lowest sort_order). */
export function getDefaultRouteTarget(menuRows, router) {
  const sorted = sortMenuRows(menuRows);
  for (const row of sorted) {
    const target = resolveMenuRouteTarget(row, router);
    if (target) return target;
  }
  if (router.hasRoute("My Tasks")) return { name: "My Tasks" };
  if (router.hasRoute("Profile")) return { name: "Profile" };
  return { name: "Sign In" };
}

export function routeRequiresMenuPermission(routeName) {
  return routeName && MENU_GATED_ROUTE_NAMES.has(routeName);
}

export function hasMenuAccessConfigured(menuRows) {
  return Array.isArray(menuRows) && menuRows.length > 0;
}

export function canAccessRouteName(routeName, menuRows, router) {
  if (!routeRequiresMenuPermission(routeName)) return true;
  if (!hasMenuAccessConfigured(menuRows)) return true;
  const allowed = getAllowedRouteNames(menuRows, router);
  return allowed.has(routeName);
}

export async function loadAndStoreMenuAccess(store, userId) {
  const uid = userId ?? store.state.user?.userId;
  const rows = await uiMenuApi.getByUser(uid);
  const list = Array.isArray(rows) ? rows : [];
  store.commit("setMenuAccess", list);
  touchMenuAccessLoadedAt();
  return list;
}

export async function ensureMenuAccess(store, router) {
  const userId = store.state.user?.userId;
  if (!userId) return [];
  const hasRows =
    Array.isArray(store.state.menuAccess) && store.state.menuAccess.length;
  if (hasRows && !isMenuAccessStale()) {
    return store.state.menuAccess;
  }
  return loadAndStoreMenuAccess(store, userId);
}

export async function navigateAfterAuth(store, router) {
  const userId = store.state.user?.userId;
  if (!userId) return { name: "Sign In" };
  const rows = await loadAndStoreMenuAccess(store, userId);
  const { loadAndStoreFeatureAccess } = await import("@/utils/featureAccess");
  await loadAndStoreFeatureAccess(store, userId);
  return getDefaultRouteTarget(rows, router);
}
